import React, { useEffect, useRef, useState } from 'react';
import { toast } from 'react-hot-toast';

/**
 * 🧭 INTEGRATSIYA SSENARIYSI oynasi (Boshqaruvchi F2, 2026-09-27).
 * Backend: services/playbooks.js, routes/integrations.js (/integrations/runs/:id).
 * Qadamlar ro'yxati + joriy savol (tugmalar) yoki forma + konsol/reja havolalari + log.
 * Har 4 soniyada yangilanadi (konsol, vazifa, karta holati serverda o'zgaradi).
 * `authFetch` har render'da yangi — ref orqali.
 */
const ST = {
  done: { i: '✓', c: 'bg-emerald-500 text-white' },
  current: { i: '▶', c: 'bg-primary-600 text-white' },
  failed: { i: '✗', c: 'bg-red-500 text-white' },
  cancelled: { i: '✖', c: 'bg-gray-400 text-white' },
  todo: { i: '', c: 'bg-gray-200 dark:bg-gray-700 text-gray-500' },
};
const STATUS = { active: '⏳ ishlamoqda', waiting: '👤 sizni kutmoqda', done: '✅ tugadi', failed: '❌ xato', cancelled: '✖ bekor qilindi' };

export default function PlaybookModal({ authFetch, apiUrl, runId, onClose, onOpenConsole, onOpenTodo }) {
  const afRef = useRef(authFetch); afRef.current = authFetch;
  const [run, setRun] = useState(null);
  const [err, setErr] = useState(null);
  const [busy, setBusy] = useState(false);
  const [fields, setFields] = useState({});
  const [showLog, setShowLog] = useState(false);

  const load = async () => {
    try {
      const r = await afRef.current(`${apiUrl}/integrations/runs/${runId}`);
      const d = await r.json();
      if (!r.ok) { setErr(d.error || 'Xato'); return; }
      setRun(d); setErr(null);
    } catch (e) { setErr('Yuklab bo\'lmadi'); }
  };
  useEffect(() => {
    load();
    const t = setInterval(load, 4000);
    return () => clearInterval(t);
    // eslint-disable-next-line
  }, [runId]);

  const send = async (body) => {
    setBusy(true);
    try {
      const r = await afRef.current(`${apiUrl}/integrations/runs/${runId}/answer`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) });
      const d = await r.json().catch(() => ({}));
      if (!r.ok) { toast.error(d.error || 'Xato'); return; }
      setRun(d); setFields({});
    } catch (e) { toast.error('Ulanib bo\'lmadi'); } finally { setBusy(false); }
  };
  const cancel = async () => {
    if (!window.confirm('Ssenariy to\'xtatilsinmi? (yaratilgan vazifa va kartalar qoladi)')) return;
    try {
      const r = await afRef.current(`${apiUrl}/integrations/runs/${runId}/cancel`, { method: 'POST' });
      const d = await r.json().catch(() => ({}));
      if (!r.ok) { toast.error(d.error || 'Xato'); return; }
      setRun(d);
    } catch (e) { toast.error('Ulanib bo\'lmadi'); }
  };

  const w = run && run.wait;
  const inp = 'w-full rounded-lg border border-gray-300 dark:border-gray-600 bg-gray-50 dark:bg-gray-700 text-gray-900 dark:text-white text-sm px-2.5 py-1.5';
  const live = run && ['active', 'waiting'].includes(run.status);

  return (
    <div className="fixed inset-0 z-[70] bg-black/40 flex items-start justify-center p-4 overflow-y-auto" onClick={onClose}>
      <div className="w-full max-w-2xl mt-10 bg-white dark:bg-gray-800 rounded-xl shadow-2xl border border-gray-200 dark:border-gray-700" onClick={(e) => e.stopPropagation()}>
        <div className="px-5 py-4 border-b border-gray-100 dark:border-gray-700 flex items-start gap-3">
          <div className="flex-1 min-w-0">
            <div className="text-lg font-semibold text-gray-900 dark:text-white">🧭 {run ? run.title : 'Ssenariy'}</div>
            {run && <div className="text-xs text-gray-500 dark:text-gray-400"><span translate="no">{run.projectName}</span> · {STATUS[run.status] || run.status}</div>}
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 text-xl leading-none px-1">✕</button>
        </div>

        {err && <div className="m-5 text-sm text-red-600">{err}</div>}
        {run && (
          <div className="p-5 space-y-4">
            <ol className="flex flex-wrap gap-x-3 gap-y-2">
              {run.steps.map((s) => {
                const st = ST[s.state] || ST.todo;
                return (
                  <li key={s.id} className="flex items-center gap-1.5 text-xs">
                    <span className={`w-5 h-5 rounded-full flex items-center justify-center text-[11px] font-bold ${st.c}`}>{st.i}</span>
                    <span className={s.state === 'current' ? 'font-semibold text-gray-900 dark:text-white' : 'text-gray-500 dark:text-gray-400'}>{s.title}</span>
                  </li>
                );
              })}
            </ol>

            {w && live && (
              <div className={`rounded-lg border p-4 ${['ask', 'form'].includes(w.kind) ? 'border-amber-300 dark:border-amber-700 bg-amber-50 dark:bg-amber-900/20' : 'border-blue-200 dark:border-blue-800 bg-blue-50 dark:bg-blue-900/20'}`}>
                <div className="font-medium text-gray-900 dark:text-white">{w.text}</div>
                {w.detail && <div className="text-sm text-gray-600 dark:text-gray-300 mt-1 whitespace-pre-wrap">{w.detail}</div>}
                {w.kind === 'form' && (
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 mt-3">
                    {(w.fields || []).map(f => (
                      <label key={f.key} className={`block ${f.textarea ? 'sm:col-span-3' : ''}`}>
                        <span className="text-xs font-medium text-gray-600 dark:text-gray-300">{f.label}</span>
                        {f.textarea ? (
                          <textarea className={inp} rows={6} placeholder={f.placeholder || ''} value={fields[f.key] || ''}
                            onChange={(e) => { const val = e.target.value; setFields(v => ({ ...v, [f.key]: val })); }} />
                        ) : (
                          <input className={inp} placeholder={f.placeholder || ''} value={fields[f.key] || ''} inputMode="numeric"
                            onChange={(e) => { const val = e.target.value.trim(); setFields(v => ({ ...v, [f.key]: val })); }} />
                        )}
                      </label>
                    ))}
                  </div>
                )}
                <div className="flex flex-wrap gap-2 mt-3">
                  {w.kind === 'form' && (
                    <button disabled={busy} onClick={() => send({ fields })}
                      className="px-3 py-1.5 rounded-lg bg-primary-700 hover:bg-primary-800 text-white text-sm font-medium disabled:opacity-50">{busy ? '⏳' : '💾 Saqlash'}</button>
                  )}
                  {(w.options || []).map(o => (
                    <button key={o.value} disabled={busy} onClick={() => send({ value: o.value })}
                      className={`px-3 py-1.5 rounded-lg text-sm font-medium disabled:opacity-50 ${w.kind === 'form' ? 'border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-200' : 'bg-primary-700 hover:bg-primary-800 text-white'}`}>{busy ? '⏳' : o.label}</button>
                  ))}
                  {w.kind === 'console' && onOpenConsole && (
                    <button onClick={() => onOpenConsole()} className="px-3 py-1.5 rounded-lg bg-red-600 hover:bg-red-700 text-white text-sm font-medium">🔐 Konsolga o'tib kirish</button>
                  )}
                  {w.kind === 'card' && onOpenTodo && (
                    <button onClick={() => onOpenTodo(run.projectId)} className="px-3 py-1.5 rounded-lg border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-200 text-sm">📋 Rejada ochish</button>
                  )}
                </div>
                {['task', 'console', 'card', 'timer'].includes(w.kind) && <div className="text-[11px] text-gray-500 dark:text-gray-400 mt-2">Bu qadam o'zi kuzatiladi — holat o'zgarganda ssenariy davom etadi.</div>}
              </div>
            )}
            {run.refs.release && run.refs.release.notes && (
              <div className="rounded-lg border border-gray-200 dark:border-gray-700 p-3">
                <div className="text-xs font-semibold text-gray-600 dark:text-gray-400 mb-1">📝 What's New (App Store va Play'ga ketadi)</div>
                <div className="text-sm text-gray-800 dark:text-gray-200 whitespace-pre-wrap" translate="no">{run.refs.release.notes}</div>
              </div>
            )}
            {run.status === 'done' && <div className="rounded-lg border border-emerald-300 dark:border-emerald-700 bg-emerald-50 dark:bg-emerald-900/20 p-4 text-sm text-emerald-800 dark:text-emerald-300">{(run.log[run.log.length - 1] || {}).text}</div>}
            {run.lastError && <div className="text-sm text-red-600 dark:text-red-400">⚠️ {run.lastError}</div>}

            <div className="flex flex-wrap gap-3 text-xs text-gray-500 dark:text-gray-400">
              {run.refs.release && run.refs.release.target && <span>📦 Versiya {run.refs.release.target}{run.refs.release.iosBuild ? ` · iOS build ${run.refs.release.iosBuild}` : ''}{run.refs.release.versionCode ? ` · Android versionCode ${run.refs.release.versionCode}` : ''}{run.refs.release.iosState ? ` · App Store: ${run.refs.release.iosState}` : ''}{run.refs.release.playStatus ? ` · Play: ${run.refs.release.playStatus}` : ''}</span>}
              {run.refs.fixTask && <span>🛠 Tuzatish: vazifa #{run.refs.fixTask}</span>}
              {run.refs.codeTask && <span>Kod: vazifa #{run.refs.codeTask}</span>}
              {run.refs.uiTask && <span>UI: vazifa #{run.refs.uiTask}</span>}
              {run.refs.consoleRun && <span>Konsol: #{run.refs.consoleRun}</span>}
              {(run.refs.cards || []).length > 0 && <span>Reja kartalari: {run.refs.cards.length}</span>}
            </div>

            <div>
              <button onClick={() => setShowLog(v => !v)} className="text-xs text-primary-700 dark:text-primary-400">{showLog ? '▾' : '▸'} Jurnal ({run.log.length})</button>
              {showLog && (
                <ul className="mt-2 space-y-1 max-h-60 overflow-y-auto">
                  {run.log.slice().reverse().map((l, i) => (
                    <li key={i} className="text-xs text-gray-600 dark:text-gray-300">
                      <span className="text-gray-400 font-mono mr-1.5">{new Date(l.at).toLocaleString('ru-RU', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })}</span>{l.text}
                    </li>
                  ))}
                </ul>
              )}
            </div>

            {live && (
              <div className="flex justify-end">
                <button onClick={cancel} className="text-xs text-gray-400 hover:text-red-600">✖ Ssenariyni to'xtatish</button>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
